import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import { OverbookingsFilterConfig } from "PFApp/booking/components/details_panel";
import { filterOverbookingsMatchingBookingsByDate } from "PFApp/booking/parts/helpers/overbookings_helper";
import { Overbooking } from "PFApp/booking/types";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useActivities } from "PFCore/hooks/queries/activities/use_activities";
import { Booking, Id } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { Option } from "PFTypes/options";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const ALL_OPTION_ID = "all";

type OptionWithOverbookings = {
  option: Option<Id>;
  overbookings: Overbooking[];
};

type UseOverbookingsFilter = {
  filterConfig?: OverbookingsFilterConfig;
  bookings: Pick<Booking, "start_date" | "end_date">[];
  overbookings: Overbooking[];
};

type UseOverbookingsFilterReturn = {
  options: OptionWithOverbookings[];
  selectedOption: Id | null;
  setSelectedOption: (id: Id) => void;
  isLoading: boolean;
};

export const useOverbookingsFilterOptions = ({
  filterConfig,
  bookings,
  overbookings
}: UseOverbookingsFilter): UseOverbookingsFilterReturn => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "details.overbookings" });

  const [selectedOption, setSelectedOption] = useState<Id | null>(null);

  const { data: activitiesData, isFetching: isActivitiesFetching } = useActivities(
    {
      filters: {
        fields: {
          [KnownFiltersV2.Id]: filterConfig?.filteredActivityIds
        }
      },
      perPage: PER_PAGE_UNPAGINATED
    },
    {
      enabled: !isEmpty(filterConfig?.filteredActivityIds),
      retry: false
    }
  );
  const activities = activitiesData?.entries.map(({ activity }) => activity);

  const bookingsGroupedByActivityId = groupBy(bookings, "activity_id");
  const bookingsForOverbookingsMap = {
    [ALL_OPTION_ID]: bookings,
    ...bookingsGroupedByActivityId
  };

  const relatedBookingsMap: Record<string, Pick<Booking, "start_date" | "end_date">[]> = pick(
    bookingsForOverbookingsMap,
    [ALL_OPTION_ID, ...(filterConfig?.filteredActivityIds ?? [])]
  );

  const overbookingsMap = mapValues(relatedBookingsMap, (bookings) =>
    filterOverbookingsMatchingBookingsByDate({
      overbookings,
      bookings
    })
  );

  const allOption: { option: Option<Id>; overbookings: Overbooking[] } = {
    option: {
      id: ALL_OPTION_ID,
      value: t("allOverbookings"),
      original: ALL_OPTION_ID
    },
    overbookings: overbookingsMap[ALL_OPTION_ID] ?? []
  };

  const activityOptions: { option: Option<Id>; overbookings: Overbooking[] }[] = activities
    ? activities.map(({ id, name }) => ({
        option: {
          id,
          value: name,
          original: id
        },
        overbookings: overbookingsMap[id] ?? []
      }))
    : [];

  const isAllAvailable =
    !isEmpty(allOption.overbookings) &&
    activityOptions.some(
      ({ overbookings: activtiyOverbookings }) => !isEqual(activtiyOverbookings, allOption.overbookings)
    );

  const options = [...(isAllAvailable ? [allOption] : []), ...activityOptions];

  useEffect(() => {
    if (isActivitiesFetching || isEmpty(options)) {
      return;
    }
    const defaultSelectedOption =
      isAllAvailable && (!!filterConfig?.showAllOverbookingsEnabled || (activities && activities?.length > 1))
        ? ALL_OPTION_ID
        : (activities?.[0]?.id ?? options[0].option.id);
    setSelectedOption(defaultSelectedOption);
  }, [filterConfig?.showAllOverbookingsEnabled, activitiesData, isActivitiesFetching, overbookings]);

  return {
    options,
    selectedOption,
    setSelectedOption,
    isLoading: isActivitiesFetching
  };
};
